/* eslint-disable react/display-name */
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  TextField,
  Box,
  CircularProgress,
  TablePagination,
} from '@mui/material';
import { gql, useQuery, useMutation } from '@apollo/client';
import {
  GetRiderOrderDetailsForRiderStatsById,
  AdminEndRiderDeliveryForOrders,
} from '../../apollo';

const GET_RIDER_ORDER_DETAILS = gql`${GetRiderOrderDetailsForRiderStatsById}`;
const ADMIN_END_RIDE = gql`${AdminEndRiderDeliveryForOrders}`;

const RIDER_ORDER_STATS = {
  OUT_FOR_DELIVERY: 1,
  DELIVERED: 2,
  CANCELLED: 3,
  REASSIGNED: 4,
};

const RIDER_RIDE_STATS = {
  PENDING: 0,
  STARTED: 1,
  ENDED: 2,
  ENDED_BY_ADMIN: 3,
};

const getOrderStatusLabel = (status) => {
  switch (Number(status)) {
    case RIDER_ORDER_STATS.OUT_FOR_DELIVERY:
      return 'Out for Delivery';
    case RIDER_ORDER_STATS.DELIVERED:
      return 'Delivered';
    case RIDER_ORDER_STATS.CANCELLED:
      return 'Cancelled';
    case RIDER_ORDER_STATS.REASSIGNED:
      return 'Reassigned';
    default:
      return 'N/A';
  }
};

const getDisplayOrderId = (orderId) => {
  if (!orderId) return 'N/A';

  const orderIdParts = String(orderId).split('-');
  return orderIdParts[orderIdParts.length - 1] || orderId;
};

const formatDateTimeIST = (dateString) => {
  if (!dateString) return 'N/A';

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return dateString;

  return `${date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: 'Asia/Kolkata',
  }).replace(/\b(am|pm)\b/i, value => value.toUpperCase())} IST`;
};

const RideOrderDetailsModal = ({ open, onClose, riderStats, onRideUpdated }) => {
  const [showEndRideForm, setShowEndRideForm] = useState(false);
  const [reason, setReason] = useState('');

  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10); // You can make this dynamic if needed

  const handleClose = () => {
    setShowEndRideForm(false);
    setReason('');
    onClose();
  };

  const { data, loading, refetch } = useQuery(GET_RIDER_ORDER_DETAILS, {
    variables: {
      skip: page * rowsPerPage,
      limit: rowsPerPage,
      riderStatsId: riderStats && riderStats._id ? riderStats._id : '',
    },
    skip: !riderStats,
    fetchPolicy: 'network-only',
  });

  const [endRide, { loading: endRideLoading }] = useMutation(ADMIN_END_RIDE, {
    onCompleted: async () => {
      if (typeof onRideUpdated === 'function') {
        await onRideUpdated();
      }
      handleClose();
    },
    onError: (error) => {
      console.error('End Ride failed:', error);
      alert('Failed to end ride. Please try again.');
    },
  });

  const handleConfirmEndRide = () => {
    if (reason.trim() !== '' && riderStats && riderStats._id) {
      endRide({ variables: { riderStatsId: riderStats._id, reason } });
    }
  };

  const isEndRideButtonDisabled =
    riderStats && (
      Number(riderStats.rideStatus) === RIDER_RIDE_STATS.ENDED ||
      Number(riderStats.rideStatus) === RIDER_RIDE_STATS.ENDED_BY_ADMIN
    );

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
    refetch({
      skip: newPage * rowsPerPage,
      limit: rowsPerPage,
      riderStatsId: riderStats._id,
    });
  };

  const riderOrders = data &&
    data.getRiderOrderDetailsForRiderStatsById &&
    data.getRiderOrderDetailsForRiderStatsById.riderOrders
    ? data.getRiderOrderDetailsForRiderStatsById.riderOrders
    : [];

  const totalOrdersCount = data &&
    data.getRiderOrderDetailsForRiderStatsById &&
    data.getRiderOrderDetailsForRiderStatsById.ordersCount
    ? data.getRiderOrderDetailsForRiderStatsById.ordersCount
    : 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      sx={{ '& .MuiPaper-root': { borderRadius: '20px' } }}
    >
      <DialogTitle sx={{ backgroundColor: '#FFFFFF', color: '#637BFE', fontWeight: 'bold', fontSize: '18px', borderRadius: '20px 20px 0 0', py: 1.5 }}>
        Ride Order Details
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ textAlign: 'center', my: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table
              sx={{
                borderCollapse: 'separate',
                borderSpacing: 0,
                '& thead': { backgroundColor: '#637BFE' },
                '& th': {
                  color: '#FFFFFF',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  padding: '8px',
                },
                '& td': {
                  fontSize: '13px',
                  color: '#000000',
                  textAlign: 'center',
                  padding: '8px',
                },
                '& tbody tr:hover': {
                  backgroundColor: '#f9fafc',
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell>Order ID</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Status Time</TableCell>
                  <TableCell>Customer Name</TableCell>
                  <TableCell>Phone no</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Address</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {riderOrders.length > 0 ? riderOrders.map((order) => (
                  <TableRow key={`${order.orderId}-${order.status}-${order.when}`}>
                    <TableCell>{getDisplayOrderId(order.orderId)}</TableCell>
                    <TableCell>{getOrderStatusLabel(order.status)}</TableCell>
                    <TableCell>{formatDateTimeIST(order.when)}</TableCell>
                    <TableCell>{order.customerName || 'N/A'}</TableCell>
                    <TableCell>{order.customerPhone || 'N/A'}</TableCell>
                    <TableCell>{order.orderAmount || 'N/A'}</TableCell>
                    <TableCell>{order.deliveryAddress || 'N/A'}</TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={7}>No rider order details found.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            <TablePagination
              component="div"
              count={totalOrdersCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[10]}
              sx={{
                '& .MuiTablePagination-toolbar': {
                  color: '#4f4f4f',
                },
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  color: '#4f4f4f',
                },
                '& .MuiInputBase-root': {
                  color: '#4f4f4f',
                },
                '& .MuiSelect-icon': {
                  color: '#4f4f4f',
                },
              }}
            />

          </>
        )}

        {showEndRideForm && (
          <Box sx={{ p: 2 }}>
            <TextField
              label="Reason to End Ride"
              fullWidth
              multiline
              minRows={3}
              margin="dense"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              InputLabelProps={{ shrink: true }}
              InputProps={{ style: { color: 'black' } }}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        {!showEndRideForm && (
          <Button
            variant="contained"
            sx={{ backgroundColor: '#D32F2F', '&:hover': { backgroundColor: '#B71C1C' } }}
            onClick={() => setShowEndRideForm(true)}
            disabled={isEndRideButtonDisabled}
          >
            END RIDE
          </Button>
        )}
        {showEndRideForm && (
          <Button
            variant="contained"
            sx={{ backgroundColor: '#D32F2F', '&:hover': { backgroundColor: '#B71C1C' } }}
            onClick={handleConfirmEndRide}
            disabled={endRideLoading || reason.trim() === ''}
          >
            {endRideLoading ? <CircularProgress size={20} /> : 'CONFIRM END RIDE'}
          </Button>
        )}
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            color: '#637BFE',
            borderColor: '#637BFE',
            '&:hover': { backgroundColor: '#E3E8FF', borderColor: '#637BFE' },
          }}
        >
          CLOSE
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RideOrderDetailsModal;
